import { Fragment, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../api/supabase'

// Address E: link each job party to ONE existing address row in BoxTop (or create it). Rule, counted by company name only:
//   one row with this company name  -> linked without asking (shown here, can be changed)
//   two or more rows                -> staff choose; every candidate is shown with its FULL address
//   none                            -> create it in BoxTop
// Postcodes are shown to help the person decide; they never decide. Nothing is ever picked by list order or "closeness".

export interface AddressCandidateView {
  id: number
  companyName: string
  lines: string[]
  city: string | null
  postcode: string | null
  country: string | null
  roleOk: boolean
  nameMatches: boolean
  postcodeMatch: boolean | null
}

export type AddressLinkStatus =
  | 'bound' | 'unique' | 'ambiguous' | 'role_missing' | 'none' | 'stale' | 'no_account' | 'no_address' | 'rows_unavailable' | 'fallback_found'

export interface AddressLinkView {
  key: 'consignor' | 'importer' | 'notify' | 'agent'
  role: 'shipper' | 'consignee' | 'notify' | 'agent'
  label: string
  blocking: boolean
  customerId: string
  customerName: string
  ours: { name: string; lines: string[]; city: string | null; postcode: string | null; country: string | null }
  account: string | null
  // hbl = searched under the Customer's account; mbl = independent of the Customer, searched under its OWN account only
  level?: 'hbl' | 'mbl' | 'plain'
  // MBL parties: 'own' = parallel level, 'customer_account' = second round (Customer's address book), 'not_found' = neither
  foundIn?: 'own' | 'customer_account' | 'not_found'
  searched?: string[]
  status: AddressLinkStatus
  bound: AddressCandidateView | null
  candidates: AddressCandidateView[]
  message: string
}

// Statuses that do NOT stop approval: linked, linkable by itself (done when the sync runs), or waiting on an earlier step that is
// settled by Approve itself (company match / job address). Everything else needs a person first.
export const addressLinksReady = (links: AddressLinkView[] | undefined): boolean =>
  (links ?? []).every(l => !l.blocking || ['bound', 'unique', 'no_account', 'no_address'].includes(l.status))

const fullAddress = (c: { lines: string[]; city: string | null; postcode: string | null; country: string | null }): string =>
  [...c.lines, c.city, c.postcode, c.country].filter(Boolean).join(', ')

const BADGE: Record<AddressLinkStatus, { t: string; c: string }> = {
  bound: { t: 'Linked', c: 'bg-green-100 text-green-800' },
  unique: { t: 'Will link automatically', c: 'bg-green-100 text-green-800' },
  ambiguous: { t: 'Choose one', c: 'bg-amber-100 text-amber-800' },
  role_missing: { t: 'Not marked in BoxTop', c: 'bg-amber-100 text-amber-800' },
  none: { t: 'Not in BoxTop yet', c: 'bg-red-100 text-red-800' },
  stale: { t: 'Link no longer valid', c: 'bg-red-100 text-red-800' },
  no_account: { t: 'Waiting for company match', c: 'bg-gray-100 text-gray-700' },
  no_address: { t: 'Choose the job address first', c: 'bg-gray-100 text-gray-700' },
  rows_unavailable: { t: 'Could not read BoxTop', c: 'bg-red-100 text-red-800' },
  fallback_found: { t: "Found in the CUSTOMER's account — check it", c: 'bg-amber-100 text-amber-900' },
}

interface Props {
  jobRef: string
  links: AddressLinkView[]
  onChanged: (links: AddressLinkView[]) => void
}

export default function BoxtopAddressLinks({ jobRef, links, onChanged }: Props) {
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<Record<string, string>>({})
  const [choice, setChoice] = useState<Record<string, number>>({})
  const [changing, setChanging] = useState<Record<string, boolean>>({})
  const [confirmCreate, setConfirmCreate] = useState<Record<string, boolean>>({})

  const call = async (action: 'bind_boxtop_address' | 'create_boxtop_address', link: AddressLinkView, boxtopAddressId?: number) => {
    setBusy(link.key); setError(e => ({ ...e, [link.key]: '' }))
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-to-boxtrax`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionData?.session?.access_token || ''}` },
        body: JSON.stringify({ action, job_ref: jobRef, address_party: link.key, boxtop_address_id: boxtopAddressId }),
      })
      const result = await resp.json()
      if (Array.isArray(result.addressLinks)) onChanged(result.addressLinks)
      if (!result.success) setError(e => ({ ...e, [link.key]: result.error || 'Failed' }))
      else { setChanging(c => ({ ...c, [link.key]: false })); setConfirmCreate(c => ({ ...c, [link.key]: false })) }
    } catch (err) {
      setError(e => ({ ...e, [link.key]: err instanceof Error ? err.message : 'Failed' }))
    } finally {
      setBusy(null)
    }
  }

  if (links.length === 0) return null
  const flagName = (l: AddressLinkView) => (l.role === 'shipper' ? 'Shipper' : 'Consignee')

  return (
    <section className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
      <div>
        <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">Addresses in BoxTop</p>
        <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
          Each party must point at one address in BoxTop. If Shipper or Consignee is not linked, this job cannot be taken into eCustoms in BoxTop.
        </p>
      </div>

      {links.map((l, i) => {
        const heading = l.level === 'mbl' && links[i - 1]?.level !== 'mbl' ? 'MBL parties — each searched under its OWN BoxTop code'
          : l.level !== 'mbl' && (i === 0 || links[i - 1]?.level === 'mbl') ? "HBL / customer parties — searched under the Customer's BoxTop code" : null
        const needsPerson = ['ambiguous', 'role_missing', 'none', 'stale', 'fallback_found'].includes(l.status)
        const showList = (needsPerson || changing[l.key]) && l.candidates.length > 0
        const picked = choice[l.key]
        return (
          <Fragment key={l.key}>
          {heading && <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 pt-1">{heading}</p>}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{l.label}</span>
              <span className="text-sm text-gray-700 dark:text-gray-300">{l.customerName}</span>
              {l.level === 'mbl' ? (
                l.foundIn === 'own' ? <span className="px-2 py-0.5 rounded bg-green-100 text-xs font-semibold text-green-800">Parallel level — own account {l.account}</span>
                : l.foundIn === 'customer_account' ? <span className="px-2 py-0.5 rounded bg-amber-200 text-xs font-semibold text-amber-900">Customer's account {l.account} — NOT parallel level</span>
                : l.foundIn === 'not_found' ? <span className="px-2 py-0.5 rounded bg-red-100 text-xs font-semibold text-red-800">Not found{l.searched?.length ? ` (searched ${l.searched.join(', ')})` : ''}</span>
                : null
              ) : l.account ? <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-xs font-mono text-gray-700 dark:text-gray-200">searched in account {l.account}</span> : null}
              {l.level === 'mbl' && l.foundIn !== 'own' && l.status !== 'bound' && !l.searched?.length && (
                <Link to={`/customers/${l.customerId}`} target="_blank" className="px-2 py-0.5 rounded bg-red-100 text-xs font-semibold text-red-800 hover:underline">Add BoxTop code →</Link>
              )}
              {l.level === 'mbl' && l.foundIn === 'not_found' && (
                <Link to={`/customers/${l.customerId}`} target="_blank" className="px-2 py-0.5 rounded bg-red-100 text-xs font-semibold text-red-800 hover:underline">Add BoxTop code →</Link>
              )}
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${BADGE[l.status].c}`}>{BADGE[l.status].t}</span>
              {!l.blocking && <span className="text-xs text-gray-500">(does not stop the sync)</span>}
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-300">
              Address on this job: <span className="text-gray-900 dark:text-white">{fullAddress({ ...l.ours, country: l.ours.country }) || '—'}</span>
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-300">{l.message}</p>

            {l.status === 'bound' && l.foundIn === 'customer_account' && (
              <div className="p-2 rounded bg-amber-100 dark:bg-amber-900/30 text-xs text-amber-900 dark:text-amber-200 font-medium">
                This address is in the CUSTOMER's address book ({l.account}), not at parallel level. It was confirmed by a person; it may be old data filed in the wrong place.
              </div>
            )}
            {l.status === 'bound' && l.bound && !changing[l.key] && (
              <div className="p-2 rounded bg-green-50 dark:bg-green-900/20 text-xs text-green-900 dark:text-green-300">
                BoxTop address {l.bound.id}: <strong>{l.bound.companyName}</strong> — {fullAddress(l.bound)}
                <button type="button" onClick={() => setChanging(c => ({ ...c, [l.key]: true }))} className="ml-3 text-blue-600 dark:text-blue-400 hover:underline">Change</button>
              </div>
            )}
            {l.status === 'unique' && l.candidates[0] && (
              <div className="p-2 rounded bg-green-50 dark:bg-green-900/20 text-xs text-green-900 dark:text-green-300">
                BoxTop address {l.candidates[0].id}: <strong>{l.candidates[0].companyName}</strong> — {fullAddress(l.candidates[0])}
              </div>
            )}

            {showList && (
              <div className="space-y-2">
                {l.candidates.map(c => (
                  <label key={c.id} className={`flex items-start gap-3 p-2.5 rounded-lg border text-sm ${
                    !c.roleOk ? 'opacity-60 border-gray-200 dark:border-gray-700'
                    : picked === c.id ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 cursor-pointer'
                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer'}`}>
                    <input type="radio" name={`addr-${l.key}`} disabled={!c.roleOk} checked={picked === c.id} onChange={() => setChoice(x => ({ ...x, [l.key]: c.id }))} />
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white">{c.companyName} <span className="text-xs font-normal text-gray-500">(BoxTop address {c.id})</span></p>
                      {c.lines.map((x, i) => <p key={i} className="text-gray-700 dark:text-gray-300">{x}</p>)}
                      <p className="text-gray-700 dark:text-gray-300">{[c.city, c.postcode, c.country].filter(Boolean).join(', ') || '—'}</p>
                      {c.postcodeMatch === true && <p className="text-xs text-green-700 dark:text-green-400">Same postcode as the address on this job</p>}
                      {c.postcodeMatch === false && <p className="text-xs text-gray-500">Different postcode from the address on this job</p>}
                      {!c.roleOk && <p className="text-xs text-amber-700 dark:text-amber-400">Not marked as {flagName(l)} in BoxTop — tick it there first</p>}
                    </div>
                  </label>
                ))}
                <button type="button" disabled={busy !== null || picked === undefined} onClick={() => call('bind_boxtop_address', l, picked)}
                  className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50">
                  {busy === l.key ? 'Saving…' : 'Use this address'}
                </button>
              </div>
            )}

            {(needsPerson || changing[l.key]) && l.account && l.level === 'mbl' && (
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Master-level parties are not created from here. Link an address that already exists in account {l.account}, or add it in BoxTop first.
              </p>
            )}
            {(needsPerson || changing[l.key]) && l.account && l.level !== 'mbl' && (
              <div className="pt-1">
                {!confirmCreate[l.key] ? (
                  <button type="button" disabled={busy !== null} onClick={() => setConfirmCreate(c => ({ ...c, [l.key]: true }))}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50">
                    {l.candidates.length > 0 ? 'None of these fit — create a new address in BoxTop' : 'Create this address in BoxTop'}
                  </button>
                ) : (
                  <div className="p-2 rounded bg-amber-50 dark:bg-amber-900/20 text-xs text-amber-900 dark:text-amber-300 space-y-2">
                    <p>
                      This adds <strong>{l.ours.name}</strong> — {fullAddress(l.ours)} to BoxTop's address list for account <strong>{l.account}</strong>, marked as {flagName(l)}.
                      BoxTop has no way to delete it again from here (staff delete it in BoxTop). Creating a second address for a company that already has one makes later choices harder.
                    </p>
                    <div className="flex gap-2">
                      <button type="button" disabled={busy !== null} onClick={() => call('create_boxtop_address', l)}
                        className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium disabled:opacity-50">
                        {busy === l.key ? 'Creating…' : 'Yes, create it'}
                      </button>
                      <button type="button" disabled={busy !== null} onClick={() => setConfirmCreate(c => ({ ...c, [l.key]: false }))} className="text-gray-600 hover:underline">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {changing[l.key] && (
              <button type="button" onClick={() => setChanging(c => ({ ...c, [l.key]: false }))} className="text-xs text-gray-500 hover:underline">Cancel change</button>
            )}
            {error[l.key] && <div className="p-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-xs whitespace-pre-wrap">{error[l.key]}</div>}
          </div>
          </Fragment>
        )
      })}
    </section>
  )
}
